import React, { useContext, useEffect, useState } from 'react';
import {
  View, StyleSheet, ViewStyle,
} from 'react-native';
import Modal from 'react-native-modal';
import { observer } from 'mobx-react-lite';

import Style from '../style';
import { MIN_INNER_WIDTH, MIN_SQUEEZE_WIDTH } from '../util/helpers';
import ButtonSmall from './ButtonSmall';
import { getBrowserInfo } from './HeaderBar';
import { IconName } from '../style/IconsRegular';
import { RootStoreContext } from '../stores/RootStore';

type DrawerStyle = 'right' | 'left';
type AnimStyle = 'slideInLeft' | 'slideInRight' | 'slideInUp' | 'slideOutLeft' | 'slideOutRight' | 'slideOutDown';

type ModalConfig = {
  pos: 'flex-start' | 'flex-end' | 'center',
  animIn: AnimStyle,
  animOut: AnimStyle,
  xIcon: IconName,
};

const CLOSE_ANIM_TIME = 300; // 300 is default animation duration
const MIN_DRAWER_WIDTH = 830;

const modalConfig = (drawer?: DrawerStyle) => {
  let conf: ModalConfig = { pos: 'center', animIn: 'slideInUp', animOut: 'slideOutDown', xIcon: 'X' };
  if (drawer === 'left') {
    conf = { pos: 'flex-start', animIn: 'slideInLeft', animOut: 'slideOutLeft', xIcon: 'CaretDoubleLeft' };
  }
  if (drawer === 'right') {
    conf = { pos: 'flex-end', animIn: 'slideInRight', animOut: 'slideOutRight', xIcon: 'CaretDoubleRight' };
  }
  return conf;
};

export const getDrawerWidth = (browserWidth: number, compactView: boolean) => (
  compactView ? browserWidth * 0.9 : Math.max(browserWidth * 0.52, MIN_DRAWER_WIDTH)
);

interface ModalViewProps {
  refName: string;
  isVisible: boolean;
  closeModal: () => void;
  children?: React.ReactNode;
  style?: ViewStyle;
  showCloseBtn?: boolean;
  drawer?: DrawerStyle;
}

const ModalView = observer(({
  refName, children, isVisible, closeModal, style = {},
  showCloseBtn = true, drawer = undefined,
}: ModalViewProps) => {
  const store = useContext(RootStoreContext);
  const { parentActiveModal } = store.uiState;
  const { browser, compactView } = getBrowserInfo();
  const [isModalVisible, setModalVisible] = useState(isVisible);

  useEffect(() => {
    store.uiState.setParentActiveModal(refName);
    const handleEscKey = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        toggleModal();
      }
    };
    window.addEventListener('keydown', handleEscKey);
    return () => {
      window.removeEventListener('keydown', handleEscKey);
      store.uiState.setParentActiveModal(refName, false);
    };
  }, []);

  const toggleModal = () => {
    store.uiState.setParentActiveModal(refName, !isModalVisible);

    setModalVisible(!isModalVisible);
    setTimeout(() => { closeModal(); }, CLOSE_ANIM_TIME);
  };

  const isDrawer = !!drawer;
  const cfg = modalConfig(drawer);

  return (
    <Modal
      animationIn={cfg.animIn}
      animationOut={cfg.animOut}
      isVisible={isModalVisible}
      backdropOpacity={0.5}
      deviceWidth={browser.width}
      deviceHeight={browser.height}
      backdropColor={parentActiveModal !== refName ? Style.Color.Transparent : Style.Color.Black}
      onBackdropPress={toggleModal}
      style={isDrawer && { margin: 0 }}
    >
      <View style={[
        styles.container,
        compactView && { maxWidth: browser.width - 16 },
        style,
        !isDrawer && { maxHeight: browser.height - 50 },
        isDrawer && {
          width: getDrawerWidth(browser.width, compactView),
          maxWidth: MIN_INNER_WIDTH,
          height: browser.height,
          alignSelf: cfg.pos,
          margin: 0,
        },
      ]}
      >
        {showCloseBtn && (
          <View style={[styles.closeButton, isDrawer && { alignItems: 'flex-start' }]}>
            <ButtonSmall
              icon={cfg.xIcon}
              onPress={toggleModal}
              iconSize={24}
              testId="ModalView.CloseModalButton"
            />
          </View>
        )}
        {children}
      </View>
    </Modal>
  );
});

export default ModalView;

const styles = StyleSheet.create({
  container: {
    borderRadius: 4,
    backgroundColor: 'white',
    alignSelf: 'center',
    minWidth: MIN_SQUEEZE_WIDTH,
    minHeight: 300,
    marginHorizontal: 8,
  },
  closeButton: {
    width: '100%',
    alignItems: 'flex-end',
    marginTop: 20,
    marginBottom: 4,
  },
});
